import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Alert, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ManagementPageLayout from '../../components/layout/ManagementPageLayout';
import ManagementPageSkeleton from '../../components/layout/ManagementPageSkeleton';
import FolderTabs from '../../components/common/FolderTabs';
import StoreSubscriptionPanel from '../../components/stores/StoreSubscriptionPanel';
import StoreCustomerPaymentsPanel from '../../components/stores/StoreCustomerPaymentsPanel';
import StoreSmsBillingSection from '../../components/stores/StoreSmsBillingSection';
import StoreService from '../../services/storeService';
import usePermissions from '../../hooks/usePermissions';
import { Permissions } from '../../constants/Permissions';
import { useToast } from '../../context/ToastContext';
import useBillingPortalLauncher from '../../hooks/useBillingPortalLauncher';
import useCheckoutLauncher from '../../hooks/useCheckoutLauncher';
import useStorePlan from '../../hooks/useStorePlan';
import { isEffectivePro, normalizeEntitlements } from '../../utils/storePlan';

const normalizeBillingStatus = (result) => ({
  hasPaymentDetails: Boolean(result?.hasPaymentDetails ?? result?.HasPaymentDetails),
  requiresPaymentMethod: Boolean(result?.requiresPaymentMethod ?? result?.RequiresPaymentMethod),
  currentPlan: result?.currentPlan ?? result?.CurrentPlan ?? null,
  currentStatus: result?.currentStatus ?? result?.CurrentStatus ?? null,
  trialExpiresAtUtc: result?.trialExpiresAtUtc ?? result?.TrialExpiresAtUtc ?? null,
  trialDaysRemaining: result?.trialDaysRemaining ?? result?.TrialDaysRemaining ?? 0,
  postTrialMonthlyAmount: result?.postTrialMonthlyAmount ?? result?.PostTrialMonthlyAmount ?? null,
  postTrialCurrency: result?.postTrialCurrency ?? result?.PostTrialCurrency ?? null,
  activePlanDiscountPercent: result?.activePlanDiscountPercent ?? result?.ActivePlanDiscountPercent ?? null,
  discountedPostTrialMonthlyAmount: result?.discountedPostTrialMonthlyAmount ?? result?.DiscountedPostTrialMonthlyAmount ?? null,
  nextBillingAtUtc: result?.nextBillingAtUtc ?? result?.NextBillingAtUtc ?? null,
  activeTeamMemberCount: result?.activeTeamMemberCount ?? result?.ActiveTeamMemberCount ?? null,
  includedTeamMembers: result?.includedTeamMembers ?? result?.IncludedTeamMembers ?? null,
  billedAdditionalSeats: result?.billedAdditionalSeats ?? result?.BilledAdditionalSeats ?? 0,
  projectedAdditionalSeats: result?.projectedAdditionalSeats ?? result?.ProjectedAdditionalSeats ?? 0,
  additionalSeatUnitAmount: result?.additionalSeatUnitAmount ?? result?.AdditionalSeatUnitAmount ?? null,
  estimatedMonthlyTotal: result?.estimatedMonthlyTotal ?? result?.EstimatedMonthlyTotal ?? null,
  cancelAtPeriodEnd: Boolean(result?.cancelAtPeriodEnd ?? result?.CancelAtPeriodEnd),
  cancelsAtUtc: result?.cancelsAtUtc ?? result?.CancelsAtUtc ?? null,
  effectivePlan: result?.effectivePlan ?? result?.EffectivePlan ?? null,
  isEffectivePro: Boolean(result?.isEffectivePro ?? result?.IsEffectivePro),
  isLapsedPro: Boolean(result?.isLapsedPro ?? result?.IsLapsedPro),
  isInPaymentGrace: Boolean(result?.isInPaymentGrace ?? result?.IsInPaymentGrace),
  additionalStoreCount: result?.additionalStoreCount ?? result?.AdditionalStoreCount ?? 0,
  additionalStoreUnitAmount: result?.additionalStoreUnitAmount ?? result?.AdditionalStoreUnitAmount ?? null,
  additionalStoreMonthlyAmount: result?.additionalStoreMonthlyAmount ?? result?.AdditionalStoreMonthlyAmount ?? null,
  aggregatedAdditionalSeats: result?.aggregatedAdditionalSeats ?? result?.AggregatedAdditionalSeats ?? null,
  isPrimaryOwnedStore: result?.isPrimaryOwnedStore ?? result?.IsPrimaryOwnedStore ?? true,
  seatBreakdown: normalizeSeatBreakdown(result?.seatBreakdown ?? result?.SeatBreakdown),
  showReactivateNudge: Boolean(result?.showReactivateNudge ?? result?.ShowReactivateNudge),
  reactivateNudgeStage: result?.reactivateNudgeStage ?? result?.ReactivateNudgeStage ?? null,
  entitlements: normalizeEntitlements(result?.entitlements ?? result?.Entitlements ?? {
    effectivePlan: result?.effectivePlan ?? result?.EffectivePlan,
    isEffectivePro: result?.isEffectivePro ?? result?.IsEffectivePro,
    isLapsedPro: result?.isLapsedPro ?? result?.IsLapsedPro,
  }),
});

const normalizeSeatBreakdown = (items) => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.map((item) => ({
    storeId: item?.storeId ?? item?.StoreId ?? null,
    storeName: item?.storeName ?? item?.StoreName ?? '',
    activeTeamMemberCount: item?.activeTeamMemberCount ?? item?.ActiveTeamMemberCount ?? 0,
    additionalSeatCount: item?.additionalSeatCount ?? item?.AdditionalSeatCount ?? 0,
  }));
};

const SUBSCRIPTION_TAB = 'subscription';
const CUSTOMER_PAYMENTS_TAB = 'customer-payments';
const NOTIFICATIONS_TAB = 'notifications';

const PaymentSettingsPage = () => {
  const { t } = useTranslation();
  const { storeId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, isOwner } = usePermissions(storeId);
  const { showError } = useToast();
  const { openBillingPortal, loading: billingPortalLoading } = useBillingPortalLauncher(storeId);
  const { startCheckout, loading: checkoutLoading } = useCheckoutLauncher(storeId);
  // Staff don't get the billing status, so the plan comes from the access status.
  const { isPro: storeIsPro, loading: storePlanLoading } = useStorePlan(storeId);

  const [billingStatus, setBillingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingStatusLoading, setBillingStatusLoading] = useState(false);

  const canManageCustomerPayments = hasPermission(Permissions.StoresUpdate);

  const entitlements = billingStatus?.entitlements;
  const isProPlan = isEffectivePro(entitlements) || billingStatus?.isEffectivePro === true;
  const allowsSms = entitlements?.allowsSms !== false;
  const showSubscriptionTab = isOwner;
  // Free stores are paid in store, so there is nothing to set up.
  const showCustomerPaymentsTab = isOwner || canManageCustomerPayments;
  const customerPaymentsLocked = showCustomerPaymentsTab && !storeIsPro;
  const showNotificationsTab = isOwner;
  const notificationsLocked = showNotificationsTab && !(allowsSms && isProPlan);

  const tabs = useMemo(() => ([
    ...(showSubscriptionTab ? [{ value: SUBSCRIPTION_TAB, label: t('paymentSettingsPage.tabs.subscription') }] : []),
    ...(showCustomerPaymentsTab ? [{
      value: CUSTOMER_PAYMENTS_TAB,
      label: t('paymentSettingsPage.tabs.customerPayments'),
      proLocked: customerPaymentsLocked,
    }] : []),
    ...(showNotificationsTab ? [{
      value: NOTIFICATIONS_TAB,
      label: t('paymentSettingsPage.tabs.notifications'),
      proLocked: notificationsLocked,
    }] : []),
  ]), [customerPaymentsLocked, notificationsLocked, showSubscriptionTab, showCustomerPaymentsTab, showNotificationsTab, t]);

  const defaultTab = tabs[0]?.value ?? SUBSCRIPTION_TAB;
  const requestedTab = searchParams.get('tab');
  const activeTab = tabs.some((tab) => tab.value === requestedTab && !tab.proLocked)
    ? requestedTab
    : defaultTab;

  const loadBillingStatus = useCallback(async () => {
    if (!storeId || !isOwner) {
      setBillingStatus(null);
      return;
    }

    try {
      setBillingStatusLoading(true);
      const result = await StoreService.getBillingStatus(storeId);
      setBillingStatus(normalizeBillingStatus(result));
    } catch (err) {
      console.error('Failed to load billing status:', err);
      setBillingStatus(null);
    } finally {
      setBillingStatusLoading(false);
    }
  }, [isOwner, storeId]);

  useEffect(() => {
    const loadPageData = async () => {
      if (!storeId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        await loadBillingStatus();
      } catch (err) {
        console.error('Failed to load payment settings:', err);
        showError(err?.response?.data?.message || t('paymentSettingsPage.error.loading'));
      } finally {
        setLoading(false);
      }
    };

    loadPageData();
  }, [loadBillingStatus, showError, storeId, t]);

  const handleTabChange = (nextTab) => {
    setSearchParams(nextTab === defaultTab ? {} : { tab: nextTab }, { replace: true });
  };

  if (loading || storePlanLoading) {
    return (
      <ManagementPageLayout title={t('paymentSettingsPage.title')} subtitle={t('paymentSettingsPage.subtitle')}>
        <ManagementPageSkeleton />
      </ManagementPageLayout>
    );
  }

  if (tabs.length === 0) {
    return (
      <ManagementPageLayout title={t('paymentSettingsPage.title')} subtitle={t('paymentSettingsPage.subtitle')}>
        <Alert severity="warning">{t('paymentSettingsPage.error.unauthorized')}</Alert>
      </ManagementPageLayout>
    );
  }

  return (
    <ManagementPageLayout title={t('paymentSettingsPage.title')} subtitle={t('paymentSettingsPage.subtitle')}>
      <FolderTabs
        tabs={tabs}
        value={activeTab}
        onChange={handleTabChange}
        contentId="payment-settings-tabs-content"
      >
        {showSubscriptionTab ? (
          <Box role="tabpanel" hidden={activeTab !== SUBSCRIPTION_TAB}>
            {activeTab === SUBSCRIPTION_TAB ? (
              <StoreSubscriptionPanel
                storeId={storeId}
                billingStatus={billingStatus}
                billingStatusLoading={billingStatusLoading}
                checkoutLoading={checkoutLoading}
                startCheckout={startCheckout}
                billingPortalLoading={billingPortalLoading}
                openBillingPortal={openBillingPortal}
                onCancelled={loadBillingStatus}
              />
            ) : null}
          </Box>
        ) : null}

        {showCustomerPaymentsTab && !customerPaymentsLocked ? (
          <Box role="tabpanel" hidden={activeTab !== CUSTOMER_PAYMENTS_TAB}>
            {activeTab === CUSTOMER_PAYMENTS_TAB ? (
              <StoreCustomerPaymentsPanel storeId={storeId} isOwner={isOwner} />
            ) : null}
          </Box>
        ) : null}

        {showNotificationsTab && !notificationsLocked ? (
          <Box role="tabpanel" hidden={activeTab !== NOTIFICATIONS_TAB}>
            {activeTab === NOTIFICATIONS_TAB ? (
              <StoreSmsBillingSection
                storeId={storeId}
                isOwner={isOwner}
                onBillingPortal={openBillingPortal}
                billingPortalLoading={billingPortalLoading}
              />
            ) : null}
          </Box>
        ) : null}
      </FolderTabs>
    </ManagementPageLayout>
  );
};

export default PaymentSettingsPage;
